import { Outlet } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { AppHeader } from "./layout/app-header";
import { AppSidebar } from "./layout/app-sidebar";
import { CommandMenu } from "./layout/command-menu";
import { QuickSearchContext } from "./layout/quick-search-context";

/** The sidebar remembers whether it was open (a cookie written by the sidebar itself). */
const sidebarWasClosed = () => document.cookie.split("; ").includes("sidebar_state=false");

/** Layout for signed-in screens (shadcn-admin style): sidebar, top bar and the current page. */
export function AppShell() {
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const quickSearch = useMemo(
    () => ({ open: quickSearchOpen, setOpen: setQuickSearchOpen }),
    [quickSearchOpen],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setQuickSearchOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <QuickSearchContext.Provider value={quickSearch}>
      <SidebarProvider defaultOpen={!sidebarWasClosed()}>
        <a
          href="#main"
          className="fixed start-44 z-999 -translate-y-52 bg-primary px-4 py-2 text-sm font-medium text-primary-foreground opacity-95 shadow transition focus:translate-y-3"
        >
          Skip to main content
        </a>
        <AppSidebar />
        <SidebarInset className="@container/content">
          <AppHeader />
          <main
            id="main"
            tabIndex={-1}
            className="w-full px-4 py-6 outline-none @7xl/content:mx-auto @7xl/content:max-w-7xl"
          >
            <Outlet />
          </main>
        </SidebarInset>
        <CommandMenu />
      </SidebarProvider>
    </QuickSearchContext.Provider>
  );
}
